"use client";

import { useState } from "react";

import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import cn from "classnames";

import { Button } from "@/components/button/button.component";
import { CheckIcon } from "@/components/icons/check.icon";
import { ChevronRightIcon } from "@/components/icons/chevronRight.icon";
import { Text } from "@/components/text/text.component";

import { PRODUCT_DRAWER_QUERY_PARAM } from "@/constants/product.constants";

import { AnswerProps, QuizOptionData } from "../quizFlow.types";
import { QuizQuestionHelperText } from "./quizQuestionHelperText.component";

export const MultipleChoice = ({
  question,
  onMultipleChoiceNext,
  initialAnswer,
}: AnswerProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const options = question.options ?? [];
  const layout = question.layout ?? "list";

  const openProductDrawer = (productId: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set(PRODUCT_DRAWER_QUERY_PARAM, productId);
    router.push(`${pathname}?${params.toString()}`);
  };

  const savedSet = new Set(
    Array.isArray(initialAnswer)
      ? (initialAnswer as string[])
      : typeof initialAnswer === "string"
        ? [initialAnswer]
        : [],
  );

  const [selected, setSelected] = useState<Set<string>>(
    () =>
      new Set(
        options
          .filter(
            (opt) =>
              (!!opt.product?.id && savedSet.has(opt.product.id)) ||
              savedSet.has(opt._key ?? opt.title) ||
              savedSet.has(opt.title),
          )
          .map((opt) => opt._key ?? opt.title),
      ),
  );

  const toggleOption = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const renderOptions = () => {
    if (!options?.length) return null;
    return options.map((option) => {
      const key = option._key ?? option.title;
      const isSelected = selected.has(key);
      const imageUrl = getImageUrl(option);
      const productId =
        question.allowProductDrawer && option.product?.id
          ? option.product.id
          : null;
      const phoneCameraOrderClass = getPhoneCameraGroupOrderClass(
        question.questionKey,
        option,
      );

      return (
        <button
          key={key}
          data-testid="multiple-choice-option"
          onClick={() => toggleOption(key)}
          className={cn(
            "border-greyscale-strength flex items-center gap-3 rounded-xl border p-4 text-left transition-colors",
            key === "opt-none" ? "order-last md:order-0" : undefined,
            phoneCameraOrderClass,
            {
              "bg-primary/10": isSelected,
              "border-greyscale-strength hover:border-secondary/50":
                !isSelected,
            },
          )}>
          <div
            className={cn(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors",
              {
                "border-secondary bg-secondary": isSelected,
                "border-gray-300": !isSelected,
              },
            )}>
            {isSelected && (
              <CheckIcon width={12} height={12} className="text-white" />
            )}
          </div>

          {imageUrl && (
            <div className="relative h-20 w-20 overflow-hidden rounded-lg">
              <Image
                src={imageUrl}
                alt=""
                fill
                className="object-cover"
                sizes="1000px"
              />
            </div>
          )}

          <div className="flex flex-1 flex-col gap-2">
            <div className="flex items-center gap-1">
              {productId ? (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    openProductDrawer(productId);
                  }}
                  className={cn(
                    "text-left",
                    isSelected ? "font-bold" : "font-medium",
                    "text-greyscale-headline cursor-pointer underline decoration-[0.5px] underline-offset-6",
                    "body4",
                  )}>
                  {option.title}
                </div>
              ) : (
                <Text
                  variant="body4"
                  className={cn(
                    isSelected ? "font-bold" : "font-medium",
                    "text-greyscale-headline",
                  )}>
                  {option.title}
                </Text>
              )}
              {productId && (
                <ChevronRightIcon
                  width={16}
                  height={16}
                  className="text-greyscale-strength"
                />
              )}
            </div>
            {option.description && (
              <Text variant="body6" className="font-thin">
                {option.description}
              </Text>
            )}
          </div>
        </button>
      );
    });
  };

  return (
    <div
      data-testid="multiple-choice"
      className="mx-auto w-full max-w-5xl lg:min-w-2xl">
      <div
        className={cn("gap-3", {
          "flex flex-col": layout === "list",
          "grid grid-cols-1 sm:grid-cols-2": layout === "grid",
        })}>
        {renderOptions()}
      </div>

      <QuizQuestionHelperText text={question.helperText} />

      <div className="fixed right-0 bottom-0 left-0 z-10 mt-8 flex justify-center bg-white py-4 md:relative md:mt-6 md:flex md:bg-transparent">
        <Button
          secondary
          data-testid="quiz-next-button"
          onClick={() => onMultipleChoiceNext(Array.from(selected))}
          className="min-w-50">
          NEXT
        </Button>
      </div>
    </div>
  );
};

const getPhoneCameraGroupOrderClass = (
  questionKey: string | undefined,
  option: QuizOptionData,
): string | undefined => {
  if (questionKey !== "protection-types" || option.product?.type !== "camera") {
    return undefined;
  }
  return "max-md:order-[100]";
};

const getImageUrl = (option: QuizOptionData): string | null => {
  const asset =
    option.product?.featuredImages?.image?.asset ?? option.icon?.asset;
  if (!asset) return null;
  if ("url" in asset && asset.url) return asset.url;
  return null;
};
